import { http } from '@/api/http';
import type { PageEnvelope } from '@/api/shared';

/**
 * Patient-record task endpoints (PTV2-1097, PTV2-1089 contract).
 *
 * The live API is rooted at `/api/v1/persons/{personGuid}/tasks` — the
 * ticket writes `/v1/patients/{guid}/tasks` but the spec ships under
 * `/persons`; this slice follows the spec. Field names on the wire are
 * snake_case (`assignee_id`, `due_date`, `entity_guid`); enum values are
 * lower_snake_case (`general`, `contact_patient`, `todo`, `done`). The
 * mapper layer translates both to the FE's camelCase / SCREAMING_SNAKE_CASE
 * view model.
 *
 * Update is RFC 6902 JSON-Patch limited to a fixed set of editable paths
 * (title, type, assignee_id, priority, due_date, description, notes,
 * status); the wrapper composes the ops from a flat partial input so
 * callers don't have to assemble JSON-Patch documents by hand.
 */

export type TaskStatusCode = 'todo' | 'done' | 'cancelled';
export type TaskPriorityCode = 'low' | 'medium' | 'high' | 'urgent';
export type TaskTypeCode =
  | 'general'
  | 'contact_patient'
  | 'schedule_appointment'
  | 'order_test'
  | 'review_results'
  | 'schedule_followup'
  | 'send_results_letter';

export interface TaskDto {
  readonly id?: string;
  readonly entity_guid?: string;
  readonly entity_type?: 'person' | 'family';
  readonly title?: string;
  readonly type?: TaskTypeCode;
  readonly assignee_id?: string | null;
  readonly priority?: TaskPriorityCode;
  readonly due_date?: string | null;
  readonly description?: string | null;
  readonly notes?: string | null;
  readonly status?: TaskStatusCode;
  readonly completed_at?: string | null;
  readonly completed_by?: string | null;
  readonly cancelled_at?: string | null;
  readonly cancelled_by?: string | null;
  readonly created_at?: string | null;
  readonly created_by?: string | null;
}

export interface TaskSummaryDto {
  readonly id: string;
  readonly title?: string;
  readonly type?: TaskTypeCode;
  readonly priority?: TaskPriorityCode;
  readonly status?: TaskStatusCode;
  // The OpenAPI `TaskSummaryDTO` names these camelCase; the per-patient slice
  // (PTV2-1097) found the live wire to be snake_case. Both spellings are kept
  // optional so the cross-patient inbox mapper can read whichever arrives.
  readonly entity_guid?: string;
  readonly entityGuid?: string;
  readonly entity_type?: 'person' | 'family';
  readonly entityType?: 'person' | 'family';
  readonly assignee_id?: string | null;
  readonly assigneeId?: string | null;
  readonly due_date?: string | null;
  readonly dueDate?: string | null;
}

export type TaskPageDto = PageEnvelope<TaskSummaryDto>;

export interface ListTasksParams {
  readonly status?: TaskStatusCode | 'all';
  readonly assignee?: string;
  readonly page?: number;
  readonly size?: number;
}

/** Flat input the mutation hook uses to compose a JSON-Patch document. */
export interface UpdateTaskInput {
  readonly title?: string;
  readonly type?: TaskTypeCode;
  readonly assigneeId?: string | null;
  readonly priority?: TaskPriorityCode;
  readonly dueDate?: string | null;
  readonly description?: string | null;
  readonly notes?: string | null;
  readonly status?: TaskStatusCode;
}

interface JsonPatchOp {
  readonly op: 'replace';
  readonly path: string;
  readonly value: unknown;
}

function buildPatch(input: UpdateTaskInput): readonly JsonPatchOp[] {
  const ops: JsonPatchOp[] = [];
  if (input.title !== undefined) ops.push({ op: 'replace', path: '/title', value: input.title });
  if (input.type !== undefined) ops.push({ op: 'replace', path: '/type', value: input.type });
  if (input.assigneeId !== undefined)
    ops.push({ op: 'replace', path: '/assignee_id', value: input.assigneeId });
  if (input.priority !== undefined)
    ops.push({ op: 'replace', path: '/priority', value: input.priority });
  if (input.dueDate !== undefined)
    ops.push({ op: 'replace', path: '/due_date', value: input.dueDate });
  if (input.description !== undefined)
    ops.push({ op: 'replace', path: '/description', value: input.description });
  if (input.notes !== undefined) ops.push({ op: 'replace', path: '/notes', value: input.notes });
  if (input.status !== undefined)
    ops.push({ op: 'replace', path: '/status', value: input.status });
  return ops;
}

export const tasksApi = {
  /**
   * GET /persons/tasks — the caller's tasks across every person they can see
   * (PTV2-1236). Same `TaskSummaryDTO` rows as the per-patient list, but each
   * carries its own `entity_guid` so the My Cases inbox can link back to the
   * owning record. `assignee` defaults server-side to the caller; the FE
   * passes it explicitly so the intent is visible at the call site.
   */
  listTasks: (
    params: ListTasksParams = {},
    options?: { signal?: AbortSignal },
  ): Promise<TaskPageDto> =>
    http.get<TaskPageDto>('/persons/tasks', {
      ...options,
      query: {
        ...(params.status !== undefined ? { status: params.status } : {}),
        ...(params.assignee !== undefined ? { assignee: params.assignee } : {}),
        ...(params.page !== undefined ? { page: params.page } : {}),
        ...(params.size !== undefined ? { size: params.size } : {}),
      },
    }),

  /** GET /persons/{guid}/tasks — paged task summaries, defaults to status=todo. */
  listPersonTasks: (
    guid: string,
    params: ListTasksParams = {},
    options?: { signal?: AbortSignal },
  ): Promise<TaskPageDto> =>
    http.get<TaskPageDto>(`/persons/${encodeURIComponent(guid)}/tasks`, {
      ...options,
      query: {
        ...(params.status !== undefined ? { status: params.status } : {}),
        ...(params.assignee !== undefined ? { assignee: params.assignee } : {}),
        ...(params.page !== undefined ? { page: params.page } : {}),
        ...(params.size !== undefined ? { size: params.size } : {}),
      },
    }),

  /** POST /persons/{guid}/tasks — create. Server-owned fields in the body are ignored. */
  createPersonTask: (
    guid: string,
    body: TaskDto,
    options?: { signal?: AbortSignal },
  ): Promise<TaskDto> =>
    http.post<TaskDto>(`/persons/${encodeURIComponent(guid)}/tasks`, body, options),

  /** GET /persons/{guid}/tasks/{id} — single-task detail. */
  getPersonTask: (
    guid: string,
    taskId: string,
    options?: { signal?: AbortSignal },
  ): Promise<TaskDto> =>
    http.get<TaskDto>(
      `/persons/${encodeURIComponent(guid)}/tasks/${encodeURIComponent(taskId)}`,
      options,
    ),

  /**
   * PATCH /persons/{guid}/tasks/{id} — RFC 6902 JSON-Patch. Callers pass a
   * flat partial input; this builds the `replace` ops over only the keys
   * present. Server returns the updated task.
   */
  updatePersonTask: (
    guid: string,
    taskId: string,
    input: UpdateTaskInput,
    options?: { signal?: AbortSignal },
  ): Promise<TaskDto> =>
    http.patch<TaskDto>(
      `/persons/${encodeURIComponent(guid)}/tasks/${encodeURIComponent(taskId)}`,
      buildPatch(input),
      {
        ...options,
        headers: { 'content-type': 'application/json-patch+json' },
      },
    ),

  /** DELETE /persons/{guid}/tasks/{id} — soft delete; idempotent on unknown id. */
  deletePersonTask: (
    guid: string,
    taskId: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(
      `/persons/${encodeURIComponent(guid)}/tasks/${encodeURIComponent(taskId)}`,
      options,
    ),
};
